import { ref } from 'vue';
import axios from 'axios';
import { useAccount } from './useAccount';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
} from 'dashboard/routes/dashboard/settings/inbox/channels/whatsapp/utils';

// Drives Meta's WhatsApp embedded-signup popup (Facebook JS SDK). FB.login()
// resolves an auth `code` while the WABA identifiers (waba_id, phone_number_id)
// arrive separately over a postMessage event — order isn't guaranteed, so we
// hold both and resolve once both are present.
//
// `runEmbeddedSignup` returns the signup credentials; the caller exchanges them
// for an inbox via `inboxes/createWhatsAppEmbeddedSignup` and owns its own UX
// (alerts, navigation, etc). Resolves `null` when the user cancels the popup;
// rejects on SDK load or signup errors. The window listener is scoped to a
// single run, so this is safe to call from anywhere without lifecycle wiring.
export function useWhatsappEmbeddedSignup() {
  const { accountId } = useAccount();
  const isAuthenticating = ref(false);

  const runEmbeddedSignup = (config = {}) => {
    if (isAuthenticating.value) return Promise.resolve(null);
    isAuthenticating.value = true;

    return new Promise((resolve, reject) => {
      let authCode = null;
      let businessData = null;
      let settled = false;
      let messageHandler;
      let businessDataTimeout;

      const settle = (fn, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(businessDataTimeout);
        window.removeEventListener('message', messageHandler);
        isAuthenticating.value = false;
        fn(value);
      };

      // Both the auth code and the business data arrive asynchronously and in
      // no fixed order; only resolve once we're holding both.
      const resolveIfReady = () => {
        if (!authCode || !businessData) return;
        const credentials = {
          code: authCode,
          business_id: businessData.business_id || '',
          waba_id: businessData.waba_id || '',
          phone_number_id: businessData.phone_number_id || '',
        };
        try {
          const redirectUri = window.localStorage.getItem(
            'chatwoot:whatsapp_embedded_signup:redirect_uri'
          );
          if (redirectUri) credentials.redirect_uri = redirectUri;
        } catch {
          // Storage may be unavailable in private browsing.
        }
        settle(resolve, credentials);
      };

      messageHandler = createMessageHandler(data => {
        if (String(data.event || '').startsWith('FINISH')) {
          const details = data.data || {};
          if (typeof details !== 'object' || Array.isArray(details)) {
            settle(reject, new Error('Invalid business data'));
            return;
          }
          businessData = {
            ...details,
            waba_id: details.waba_id || details.waba_ids?.[0],
            phone_number_id:
              details.phone_number_id || details.phone_number_ids?.[0],
          };
          resolveIfReady();
        } else if (data.event === 'CANCEL') {
          settle(resolve, null);
        } else if (data.event === 'error') {
          settle(reject, new Error(data.error_message || 'Signup error'));
        }
      });

      window.addEventListener('message', messageHandler);

      (async () => {
        try {
          let signupConfig = config;
          if (!Object.keys(signupConfig).length) {
            const { data } = await axios.get(
              `/api/v1/accounts/${accountId.value}/whatsapp_config`
            );
            signupConfig = {
              appId: data.whatsapp_app_id,
              configId: data.whatsapp_configuration_id,
              apiVersion: data.whatsapp_api_version,
            };
          }
          if (settled) return;
          await setupFacebookSdk(
            signupConfig.appId || window.chatwootConfig?.whatsappAppId,
            signupConfig.apiVersion || window.chatwootConfig?.whatsappApiVersion
          );
          if (settled) return;
          authCode = await initWhatsAppEmbeddedSignup(
            signupConfig.configId ||
              window.chatwootConfig?.whatsappConfigurationId
          );
          if (settled) return;
          resolveIfReady();
          // Some Meta flows return only the code; the server can infer the
          // authorized WABA from that token, as in the current live flow.
          if (!settled) {
            businessDataTimeout = setTimeout(() => {
              businessData = businessData || {};
              resolveIfReady();
            }, 10000);
          }
        } catch (error) {
          // FB.login() rejects with 'Login cancelled' when the user dismisses
          // the popup — treat it as a cancel rather than an error.
          if (error.message === 'Login cancelled') {
            settle(resolve, null);
          } else {
            settle(reject, error);
          }
        }
      })();
    });
  };

  return { isAuthenticating, runEmbeddedSignup };
}
